import { ListWrapper, unwrap } from '@summer/react-kit';
import { useQuery, useMutation } from '@tanstack/react-query';
import { pipe, map, sortBy } from 'ramda';
import { VerificationDTO } from 'src/common/models/verificationModels';
import { publicApiPath } from 'src/common/publicApiPath';
import { verificationQueryKeys } from 'src/common/verificationQueryKeys';
import { AuthorizedBaseAxios, BaseAxios, flatten } from 'src/config/axios';
import {
  APIAspspItemDTO,
  BankResponse,
  PublicAspspItemDTO,
  logotypes,
} from 'src/pages/AspspSelectPage/aspspModels';

const mapToAspspItem: (dto: APIAspspItemDTO) => PublicAspspItemDTO = (
  item
) => ({
  ...item,
  logo: logotypes[item.name] || null,
});

const getListOfAspsps = (verificationId: string) =>
  BaseAxios.get<ListWrapper<APIAspspItemDTO>>(
    `${publicApiPath}/aspsps?id=${verificationId}`
  ).then(
    pipe(flatten, unwrap, map(mapToAspspItem), (aspsps) =>
      sortBy((x) => x.order, aspsps)
    )
  );

export const useAspspListQuery = (verificationId?: string) =>
  useQuery(
    verificationQueryKeys.aspspList(verificationId),
    () => (verificationId ? getListOfAspsps(verificationId) : undefined),
    {
      enabled: Boolean(verificationId),
    }
  );

const getListOfUnknownBanks = (verificationId: string | null) =>
  BaseAxios.get<BankResponse>(
    `${publicApiPath}/aspsps?id=${verificationId}`
  );

export const listOfUnknownBanks = async (verificationId: string | null) => {
  return await getListOfUnknownBanks(verificationId);
};


interface SendSelectedAspspDTO {
  aspspId: string;
  verificationId: string;
  otherBank?: string;
}

export const sendSelectedAspsp = ({
  aspspId,
  verificationId,
  otherBank
}: SendSelectedAspspDTO) =>
  AuthorizedBaseAxios.post<VerificationDTO>(
    `${publicApiPath}/processes/basic`,
    {
      aspspId,
      verificationId,
      otherBank,
    }
  ).then(flatten);

export const useSendAspspMutation = () => useMutation(sendSelectedAspsp);

export const getOpenBankingUrl = (verificationId: string) =>
  AuthorizedBaseAxios.get<VerificationDTO>(
    `${publicApiPath}/${verificationId}/openBankingUrl`
  ).then(flatten);

export const useGetOpenBankingUrlQuery = ({
  verificationId,
  enabled
}: {
  verificationId?: string;
  enabled: boolean;
}) =>
  useQuery(
    verificationQueryKeys.aspspOpenBankingUrl(verificationId),
    () => (verificationId ? getOpenBankingUrl(verificationId) : undefined),
    {
      enabled,
      onError: (err) => {
        console.log(err)
      }
    }
  );
